import { useState } from 'react'
import { useInstallPrompt } from '@/features/install/useInstallPrompt'
import { useT } from '@/i18n/useT'
import { Button } from '@/components/ui'

const DISMISSED_KEY = 'bella.installDismissed'

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * A single, dismissible invitation to install — shown above the tab bar rather
 * than over the content.
 *
 * Deliberately dismissible and remembered: an install nag that reappears on
 * every visit is how people learn to ignore the whole bottom of the screen.
 */
export function InstallPrompt() {
  const t = useT()
  const { canInstall, needsManualSteps, install } = useInstallPrompt()
  const [dismissed, setDismissed] = useState(wasDismissed)
  const [showIosSteps, setShowIosSteps] = useState(false)

  if (dismissed || (!canInstall && !needsManualSteps)) return null

  function dismiss() {
    setDismissed(true)
    try {
      localStorage.setItem(DISMISSED_KEY, '1')
    } catch {
      // Forgetting the dismissal is a small annoyance, not a failure.
    }
  }

  return (
    <div className="surface-raised border-hair border-t p-3">
      <div className="flex items-start gap-3">
        <img src="/icons/icon-192.png" alt="" className="size-10 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{t('install.title')}</p>
          <p className="text-muted text-xs">{t('install.hint')}</p>

          {showIosSteps && (
            <ol className="text-muted mt-2 list-decimal space-y-1 pl-4 text-xs">
              <li>{t('install.iosStep1')}</li>
              <li>{t('install.iosStep2')}</li>
            </ol>
          )}

          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              onClick={() => {
                if (canInstall) void install()
                else setShowIosSteps(true)
              }}
            >
              {t('install.button')}
            </Button>
            <Button size="sm" variant="ghost" onClick={dismiss}>
              {t('install.dismiss')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

/** The same capability, offered permanently in Settings rather than as a nag. */
export function InstallSection() {
  const t = useT()
  const { canInstall, needsManualSteps, installed, install } = useInstallPrompt()

  return (
    <section className="border-hair border-b p-4">
      <h2 className="mb-1 font-medium">{t('install.title')}</h2>
      {installed ? (
        <p className="text-muted text-sm">{t('install.installed')}</p>
      ) : needsManualSteps ? (
        <>
          <p className="text-muted mb-2 text-sm">{t('install.iosTitle')}</p>
          <ol className="text-muted list-decimal space-y-1 pl-4 text-sm">
            <li>{t('install.iosStep1')}</li>
            <li>{t('install.iosStep2')}</li>
          </ol>
        </>
      ) : (
        <>
          <p className="text-muted mb-3 text-sm">{t('install.hint')}</p>
          <Button size="sm" disabled={!canInstall} onClick={() => void install()}>
            {t('install.button')}
          </Button>
        </>
      )}
    </section>
  )
}
